// Auth namespace — shared by Login.tsx and Register.tsx (public pages).
// Keys mirror pl/auth.ts exactly. Professional product English.
export const auth = {
  login: {
    heading: 'Welcome back',
    emailLabel: 'Email',
    emailPlaceholder: 'you@yourlabel.com',
    passwordLabel: 'Password',
    forgot: 'Forgot your password?',
    submit: 'Sign in',
    submitBusy: 'Signing in…',
    noAccount: 'Don’t have an account?',
    signUp: 'Sign up free',
    // About the credentials only — shorter than before (the plan's wording,
    // artboard 04b). An unconfirmed address has its OWN sentence and its own way
    // out, so this one can finally be true about the password.
    error: 'Wrong email or password.',
    // Artboard 04c: the state with a resend has to say WHY the sign-in did not go through.
    unverified: 'That address is not confirmed.',
    // The way out of this screen's one dead end: the account exists, the password is
    // right, and signing in is impossible because the address is unconfirmed. The
    // endpoint (`/auth/resend-verification`) has been public all along — what was
    // missing was a place to use it from.
    resend: 'Send the link again',
    resendBusy: 'Sending…',
    resendSent: 'New link sent. Check your inbox and spam.',
    // 5 attempts per hour (server-side limiter). The sentence says what to do next,
    // not "error 429".
    resendFailed: 'We could not send the link. Try again in a moment.',
  },
  // Password recovery — TWO screens inside the SPA (it used to be a link OUT of
  // the app to a server-rendered page with a different layout and a Polish flash).
  forgot: {
    heading: 'Forgot your password?',
    emailLabel: 'Email',
    submit: 'Send the link',
    submitBusy: 'Sending…',
    // NEVER reveal whether the account exists — the backend answers identically
    // either way (anti-enumeration §9) and this screen must not give it away.
    doneHeading: 'Check your inbox',
    // The address is bold inside the sentence, so the sentence has two halves (artboard 05b).
    doneBefore: 'If such an account exists, the link is already on its way to ',
    doneAfter: '.',
    // A security fact, not an ornament: it stands after the link is sent and at a dead link.
    linkLife: 'The link works for 30 minutes, once.',
    backToLogin: 'Back to sign in',
    // "reach" is banned vocabulary (invariant #4) and the lint is right to flag
    // it even in this sense — the word does not belong in this product's copy.
    networkError: 'Could not connect to the server. Try again in a moment.',
  },
  reset: {
    heading: 'Set a new password',
    passwordLabel: 'New password',
    confirmLabel: 'Repeat password',
    // The rule stated UP FRONT, not revealed by a rejection.
    hint: 'At least 8 characters, a digit and a capital letter.',
    mismatch: 'The passwords do not match.',
    weak: 'That password is too weak - at least 8 characters, with a digit and a capital letter.',
    submit: 'Save new password',
    submitBusy: 'Saving…',
    deadHeading: 'This link no longer works',
    dead: 'The password link is invalid, already used, or expired.',
    requestNew: 'Send a new link',
    signOutNote: 'Changing your password signs out every device.',
  },
  // Messages shown on return from an email link (query string; see _spa_login in app/auth/routes.py).
  banner: {
    verified: 'Email confirmed.',
    verifyExpired: 'This link no longer works - you asked for a new one, or it has expired. Sign in and request a new one.',
    verifyError: 'That verification link is invalid or expired. Sign in and request a new one.',
    passwordReset: 'Password changed. Sign in with the new one.',
    emailChanged: 'Email address changed. Sign in with the new address.',
    emailTaken: 'That email address is already taken - the change was not applied.',
    emailChangeError: 'That email-change link is invalid or expired.',
  },
  register: {
    heading: 'Create account',
    emailLabel: 'Email',
    emailPlaceholder: 'you@yourlabel.com',
    passwordLabel: 'Password',
    acceptPrefix: 'I accept the',
    terms: 'Terms',
    acceptAnd: 'and',
    privacy: 'Privacy Policy',
    submit: 'Create account',
    submitBusy: 'Creating…',
    // The reassurance layer UNDER the button (artboards 06a, 06ab, 06b): one sentence
    // about the card and the name of the document you are signing up under. It is NOT
    // a consent — the consent sentences sit above it and are a protected zone.
    noCard: 'No card.',
    haveAccount: 'Already have an account?',
    signIn: 'Sign in',
    error: 'The account could not be created. Try again.',
    // Errors UNDER THE FIELDS instead of one collective sentence (MAPA 37, new copy).
    err: {
      email: 'Enter a valid email address.',
      terms: 'Tick the consent to create an account.',
    },
    check: {
      heading: 'Check your inbox',
      // The address is bold inside the sentence (artboard 06c).
      sentBefore: 'We sent a link to ',
      sentAfter: '.',
      resend: 'Send the link again',
      resendBusy: 'Sending…',
      // A 60 s client-side hold (the countdown on the button) — no promise about a server limit.
      resendWait: 'Send the link again ({s} s)',
      resendFailed: 'Could not send the link. Try again in a moment.',
      wrong: 'Wrong address?',
      again: 'Register again',
    },
  },
}
